<template>

  <div class="settlements">
    <app-header-container>
      <span>{{ $t('views.Dashboard.Settlements.title') }}</span>

      <template v-if="screen.breakPoint.tabPort && contracts.length > 1" #controls>
        <autocomplete not-empty value-field="id" item-text="fullName" v-model="currContractId" :items="contracts" :placeholder="$t('views.Orders.contractSelect.label')"></autocomplete>
      </template>
    </app-header-container>

    <app-hero-container>
      <PuSkeletonTheme color="rgba(223, 223, 223, 55%)" highlight="rgba(223, 223, 223, 85%)">
      <div class="settlements-deposit">
        <div class="settlements-deposit__inner">
          <deposit-balance v-if="screen.breakPoint.tabPort"></deposit-balance>
          <swiper :active-item-index="activeContractIndex" @change="onChangeContract" v-else>
            <swiper-item v-for="contractId of contractIds" :key="contractId">
              <deposit-balance mobile :contract="contractId"></deposit-balance>
            </swiper-item>
          </swiper>

          <div class="d-flex align-center mt-2 direction-column" v-if="settlements">
            <div class="d-flex align-center">
              <div class="text--small mr-2 text--right">
                <span class="text-color-silver">
                  {{ $t('views.Dashboard.Settlements.depositRecommend.p1') }} <br>{{ $t('views.Dashboard.Settlements.depositRecommend.p2') }}:
                </span>
                <span v-if="$apollo.queries.settlements.loading"><PuSkeleton height="1.5rem" width="3.5rem" /></span>
                <span v-else class="text--medium">{{toCurrency(settlements.forecastDeposit, settlements.partnerContract.currency)}}</span>
              </div>
              <v-button :disabled="$apollo.queries.settlements.loading" size="large" @click="showAddInvoiceModal()">{{ $t('views.Dashboard.Settlements.makeADeposit') }}</v-button>
            </div>
          </div>
        </div>
      </div>
      </PuSkeletonTheme>

      <template v-slot:footer>
        <PuSkeletonTheme color="rgba(223, 223, 223, 55%)" highlight="rgba(223, 223, 223, 85%)">
          <div class="settlements-deposit__controls text--bold d-flex justify-center" v-if="settlements">
            <div class="d-flex align-center">
              <div class="label text-color-silver mr-1" >{{ $t('views.Dashboard.Settlements.currentOrders') }}</div>
              <div class="mr-1" v-if="$apollo.queries.settlements.loading"><PuSkeleton height="2.1rem" width="10rem" /></div>
              <v-button v-else :disabled="!settlements.clientOrdersCheckCount || settlements.clientOrdersCheckCount < 1" size="xs-small" outline class="mr-1" @click="$router.push({ name: 'orders', query: { status: 'check', contract: currContractId } })">
                {{ $t('views.Dashboard.Settlements.verification') }} <span class="text-color-black ml-05">{{settlements.clientOrdersCheckCount}}</span>
              </v-button>
              <div v-if="$apollo.queries.settlements.loading"><PuSkeleton height="2.3rem" width="10rem" /></div>
              <v-button v-else :disabled="!settlements.clientOrdersPendingCount || settlements.clientOrdersPendingCount < 1" size="xs-small" outline @click="$router.push({ name: 'orders', query: { status: 'pending', contract: currContractId } })">
                {{ $t('views.Dashboard.Settlements.pending') }} <span class="text-color-black ml-05">{{settlements.clientOrdersPendingCount}}</span>
              </v-button>
            </div>
          </div>
        </PuSkeletonTheme>
      </template>
    </app-hero-container>

    <app-actions-container>
        <template v-slot:interactiveBlock>
          <div class="d-flex align-end" v-if="settlements">
            <v-button size="xs-small" outline class="mr-1">{{ $t('views.Dashboard.Settlements.verification') }} <span class="text-color-black ml-05">{{settlements.clientOrdersCheckCount}}</span></v-button>
            <v-button size="xs-small" outline>{{ $t('views.Dashboard.Settlements.pending') }} <span class="text-color-black ml-05">{{settlements.clientOrdersPendingCount}}</span></v-button>
          </div>
        </template>
    </app-actions-container>

    <payments-list date-field="date" :payments="payments ? payments.content : []" :loading="loading"></payments-list>
    <add-invoice
      v-if="settlements"
      :loading="invoiceLoading"
      :value="showAddInvoice"
      :defaultAmount="settlements.forecastDeposit"
      :currency="settlements.partnerContract.currency"
      :minimum="settlements.minDepositAmount"
      :invoiceLink="invoiceLink"
      @add="addInvoice"
      @onClose="closeAddInvoice"
      @clear="invoiceLink = null"
      ></add-invoice>
    <list-footer :loading="fetchingMore || $apollo.queries.payments.loading" :total="!payments ? 0 : payments.totalElements"></list-footer>
  </div>
</template>

<script>
import AppHeaderContainer from '@/ui/layout/AppHeaderContainer'
import AppActionsContainer from '@/ui/layout/AppActionsContainer'
import AppHeroContainer from '@/ui/layout/AppHeroContainer'

import ListFooter from '@/ui/slots/List/ListFooter'
import { Swiper, SwiperItem } from '@/ui/slots/Swiper'

import DepositBalance from '@/widgets/DepositBalance'

import vButton from '@/ui/controls/vButton'
import Autocomplete from '@/ui/controls/Autocomplete'

import PaymentsList from '@/components/PaymentsList'
import AddInvoice from '@/components/AddInvoice'

import toCurrency from '@/mixins/toCurrency'
import screen from '@/mixins/screen'
import currContractId from '@/mixins/currContractId'
import contractIds from '@/mixins/contractIds'
import contracts from '@/mixins/contracts'
import currencySymbol from '@/mixins/currencySymbol'
import fetchMore from '@/mixins/fetchMore'
import { notifyGraphqlError } from '@/mixins/handleGraphqlError'

import { BillsGQL, PaymentsGQL, SettlementsGQL } from '@/graphql/queries'
import { RechargeDepositGQL } from '@/graphql/mutations'

const pageSize = 15

export default {
  mixins: [
    toCurrency,
    screen,
    currContractId,
    currencySymbol,
    fetchMore,
    notifyGraphqlError,
    contractIds,
    contracts
  ],

  components: {
    AppHeaderContainer,
    AppActionsContainer,
    AppHeroContainer,

    DepositBalance,

    PaymentsList,
    AddInvoice,
    ListFooter,

    Swiper,
    SwiperItem,

    vButton,
    Autocomplete
  },

  data () {
    return {
      routeLiving: false,
      refetching: false,
      showAddInvoice: false,
      invoiceLink: null,
      invoiceLoading: false
    }
  },

  apollo: {
    payments: {
      query: PaymentsGQL,
      variables () {
        return {
          params: {
            page: 0,
            partnerContractId: this.currContractId,
            size: pageSize
          }
        }
      },
      update: data => data.merchant.payments,
      skip () {
        return !this.currContractId
      }
    },

    settlements: {
      query: SettlementsGQL,
      variables () {
        return {
          params: {
            partnerContractId: this.currContractId
          }
        }
      },
      update: data => data.merchant.settlements,
      skip () {
        return !this.currContractId
      }
    },

    bills: {
      query: BillsGQL,
      update: data => data.merchant.bills,
      variables () {
        return {
          params: {
            page: 0,
            size: 15
          }
        }
      },
      skip () {
        return !this.hasActiveContracts
      }
    }
  },

  computed: {
    activeContractIndex () {
      return this.contractIds.indexOf(+this.currContractId)
    },

    loading () {
      return (!this.payments && true) || (this.$apollo.queries.payments && this.$apollo.queries.payments.loading && !this.fetchingMore)
    },

    loadingMore () {
      return this.payments && this.$apollo.queries.payments && this.$apollo.queries.payments.loading && this.fetchingMore
    }
  },

  methods: {
    onChangeContract (index) {
      this.setContract(this.contractIds[index])
    },

    showMore () {
      const params = {
        page: 0,
        partnerContractId: this.currContractId,
        size: pageSize
      }

      if (this.payments) {
        this.fetchMore({
          params,
          rootField: 'merchant',
          apolloQueryField: 'payments',
          currPage: this.payments.currentPage
        })
      }
    },

    showAddInvoiceModal () {
      this.showAddInvoice = true
    },

    async addInvoice (invoiceData) {
      try {
        this.invoiceLoading = true
        const { data } = await this.$apollo.mutate({
          mutation: RechargeDepositGQL,
          variables: {
            params: invoiceData
          },
          fetchPolicy: 'no-cache'
        })

        this.invoiceLoading = false
        this.invoiceLink = this.$getEnv.billPath + data.rechargeDeposit.url
        await this.$apollo.queries.bills.refetch()
      } catch (e) {
        this.invoiceLoading = false
        this.notifyGraphqlError(e)
      }
    },
    closeAddInvoice () {
      this.invoiceLink = null
      this.showAddInvoice = false
    }
  },

  mounted () {
    document.addEventListener('reachedBottom', this.showMore)
  },

  beforeDestroy () {
    document.removeEventListener('reachedBottom', this.showMore)
  }
}
</script>
